// Detects surveys that were uploaded before the tool was fully downloaded.
//
// The app creates a survey record as soon as a run starts, and it can be
// uploaded at any point after that — including before the operator has pulled
// the sensor data off the tool. Those partial surveys reach the approval page
// looking superficially normal but with nothing to approve, so it's worth
// saying plainly what is missing rather than rendering empty tables and charts.
//
// The strongest tell is the battery log: the app records the battery at the
// start of a run and again at the end of the download, so a survey with no
// sensor records and fewer than two battery readings never got downloaded at
// all, as opposed to being downloaded and genuinely empty.
//
// The sentences are resolved in the current language when assessSurvey runs,
// which is during render, so they follow a language change like any component.

import i18n from './i18n';
import { isHddDeviceType } from './surveyQa';

const MIN_BATTERY_RECORDS = 2;

const t = (key, options) => i18n.t(key, options);

// HDD is a realtime service: the tool is merged live over the wireline, so shots
// land straight in SavedShots and the survey never carries the raw GyroRecords
// or DepthTimeRecords a post-run download produces. Their absence is normal, not
// a sign of a truncated upload — and there is nothing to re-source from either,
// because each HDD shot is a discrete gyrocompass operation rather than one
// record picked out of a continuous log. The DeviceType constant lives in
// surveyQa.js, which also gives HDD its own Earth-rate QA target.
export function isHddSurvey(survey) {
    return !!survey && isHddDeviceType(survey.DeviceType);
}

// GammaTrak reports DeviceType 14. The name is checked too because the rest of
// the app identifies tool classes by DeviceName (see the FastTrak checks), and
// either one arriving alone should still be recognised.
const GAMMA_DEVICE_TYPE = 14;

const count = (value) => (Array.isArray(value) ? value.length : 0);

// A gamma logger is a different kind of instrument: it streams a counts-per-second
// log for the whole run rather than taking discrete shots, so it has no saved
// shots to approve and nothing to run minimum curvature over.
export function isGammaSurvey(survey) {
    if (!survey) return false;
    return survey.DeviceType === GAMMA_DEVICE_TYPE
        || (survey.DeviceName || '').startsWith('GammaTrak');
}

export function assessSurvey(survey) {
    if (!survey || !survey.Name) {
        return { hasSurvey: false, isIncomplete: false, headline: null, issues: [] };
    }

    const isContinuous = (survey.DeviceName || '').startsWith('FastTrak');
    const isGamma = isGammaSurvey(survey);
    const isHdd = isHddSurvey(survey);
    // Continuous tools stream pre-calculated OrientationRecords; multishot tools
    // log raw GyroRecords; gamma tools log GammaRecords. Any of the three counts
    // as "the tool's data made it across".
    const gyroRecords = count(survey.GyroRecords);
    const orientationRecords = count(survey.OrientationRecords);
    const gammaRecords = count(survey.GammaRecords);
    const sensorRecords = gyroRecords + orientationRecords + gammaRecords;
    const depthTimeRecords = count(survey.DepthTimeRecords);
    const batteryRecords = count(survey.BatteryRecords);
    const savedShots = count(survey.SavedShots);

    let sensorKey = 'gyro';
    if (isGamma) sensorKey = 'gamma';
    else if (isContinuous) sensorKey = 'orientation';
    const sensor = t(`completeness.sensor.${sensorKey}`);

    const issues = [];
    let headline = null;

    // The download heuristics below only make sense for tools that are read back
    // after a run. An HDD survey is merged live, so it legitimately arrives with
    // no sensor records and no depth marks at all.
    if (isHdd) {
        // Nothing to check but the shots themselves.
    } else if (sensorRecords === 0 && batteryRecords < MIN_BATTERY_RECORDS) {
        headline = t('completeness.neverDownloadedHeadline');
        issues.push({
            level: 'danger',
            text: t('completeness.neverDownloaded', { sensor, count: batteryRecords })
        });
    } else if (sensorRecords === 0) {
        headline = t('completeness.noSensorDataHeadline');
        issues.push({
            level: 'danger',
            text: isGamma
                ? t('completeness.noGammaRecords')
                : t('completeness.noSensorRecords', { sensor })
        });
    }

    // Depth marks matter to a gamma run too — without them the counts log cannot
    // be tied to a depth — but their absence is not fatal the way it is for a
    // shot-based tool, where there is then nothing to correlate a shot against.
    if (depthTimeRecords === 0 && !isHdd) {
        if (!headline) headline = t('completeness.noDepthMarksHeadline');
        issues.push({
            level: isGamma ? 'warning' : 'danger',
            text: isGamma ? t('completeness.noDepthMarksGamma') : t('completeness.noDepthMarks')
        });
    }

    // A gamma log has no shots by design, so silence is correct there; for every
    // other tool an empty shot list is the whole point of the page missing.
    if (!isGamma) {
        if (savedShots === 0 && !headline) {
            headline = t('completeness.noSavedShotsHeadline');
        }
        if (savedShots === 0) {
            issues.push({
                level: 'warning',
                text: t('completeness.noSavedShots')
            });
        }
    }

    if (batteryRecords < MIN_BATTERY_RECORDS && sensorRecords > 0) {
        issues.push({
            level: 'warning',
            text: t('completeness.shortBattery', { count: batteryRecords })
        });
    }

    return {
        hasSurvey: true,
        isIncomplete: issues.length > 0,
        headline,
        issues,
        counts: { gyroRecords, orientationRecords, gammaRecords, depthTimeRecords, batteryRecords, savedShots }
    };
}

// Start and end battery voltage, for the survey metadata panel.
//
// A tool that starts a run on a low battery and dies before the download is a
// common cause of an incomplete survey, so the pair of readings either side of
// a run is a useful diagnostic. Only rendered for the one- or two-reading case
// that a start/end pair produces: some tools log the battery continuously and
// hundreds of readings say nothing useful at a glance.
export function batterySummary(survey) {
    const records = Array.isArray(survey && survey.BatteryRecords) ? survey.BatteryRecords : [];
    if (records.length === 0 || records.length > 2) {
        return null;
    }
    // Sorted rather than trusting insertion order. Note the field is spelled
    // "Recieved" in the stored data.
    const volts = [...records]
        .sort((a, b) => new Date(a.Recieved) - new Date(b.Recieved))
        .map(record => Number(record.BatteryVolts))
        .filter(Number.isFinite);
    if (volts.length === 0) {
        return null;
    }
    return volts.map(v => `${v.toFixed(2)}V`).join(' / ');
}

// True when there is nothing for the record charts to plot. Callers should show
// a placeholder rather than mounting a chart with empty series.
export function hasPlottableRecords(survey) {
    if (!survey) return false;
    if (isHddSurvey(survey)) return false;
    if (isGammaSurvey(survey)) return count(survey.GammaRecords) > 0;
    const isContinuous = (survey.DeviceName || '').startsWith('FastTrak');
    return count(isContinuous ? survey.OrientationRecords : survey.GyroRecords) > 0;
}
